/**
 * NavDropdown — reusable hover/click dropdown for the navbar.
 *
 * Used for both the "Sell ▾" entry (Sell my home / List with us) and the
 * "AI Tools ▾" entry (Daily Doorstep / Style Quiz / Design my room /
 * Compare / Yields / Map). Built to look identical to
 * MarketIntelligenceDropdown so the navbar feels coherent.
 *
 * Props:
 *  - label: nav-bar label text
 *  - Icon: leading lucide icon component (optional)
 *  - items: [{ to, Icon, title, blurb, badge?, accent? }]
 *  - linkClass: passes through the brand link colour from Navbar
 *  - transparent: navbar is in its translucent state (homepage scroll-top)
 *  - mobile: render flat inline accordion variant for the mobile drawer
 *  - onItemClick: closes the mobile drawer when an item is tapped
 *  - testId: data-testid prefix for the root + items
 *  - accent: tailwind colour name used for the icon bubble + hover ('purple' | 'amber')
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { prefetchRoute } from '../utils/prefetch';

const ACCENT = {
  purple: {
    bubbleBg: 'bg-[#F5EFFF] text-[#5b35a8]',
    bubbleHover: 'group-hover:bg-[#7042cc] group-hover:text-white',
    rowHover: 'hover:bg-[#F5EFFF]',
    labelText: 'text-[#5b35a8]',
  },
  amber: {
    bubbleBg: 'bg-amber-50 text-amber-700',
    bubbleHover: 'group-hover:bg-amber-600 group-hover:text-white',
    rowHover: 'hover:bg-amber-50',
    labelText: 'text-amber-700',
  },
};

const NavDropdown = ({
  label,
  Icon,
  items,
  linkClass,
  transparent: _transparent,
  mobile = false,
  onItemClick,
  testId = 'nav-dropdown',
  accent = 'purple',
  triggerHighlight = false,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const colors = ACCENT[accent] || ACCENT.purple;

  useEffect(() => {
    if (mobile) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [mobile]);

  if (mobile) {
    return (
      <div className="border-l-2 border-[#7042cc]/30 pl-3 my-1" data-testid={`mobile-${testId}-section`}>
        <div className={`text-[10px] uppercase tracking-widest font-semibold ${colors.labelText} mb-1`}>
          {label}
        </div>
        {items.map(({ to, Icon: ItemIcon, title }) => (
          <Link
            key={to}
            to={to}
            onClick={onItemClick}
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-[#7042cc]"
            data-testid={`mobile-${testId}-${title.toLowerCase().replace(/ /g, '-')}`}
          >
            {ItemIcon ? <ItemIcon className="h-4 w-4" /> : null}
            <span>{title}</span>
          </Link>
        ))}
      </div>
    );
  }

  // Highlighted trigger (used for "Sell ▾" so the USP visually pops)
  const triggerClass = triggerHighlight
    ? `inline-flex items-center gap-1 font-semibold text-[13px] whitespace-nowrap px-2.5 py-1 rounded-full bg-amber-100/90 text-amber-800 hover:bg-amber-200 border border-amber-300/60 transition-colors`
    : `inline-flex items-center gap-1 ${linkClass}`;

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`${testId}-root`}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClass}
        data-testid={`${testId}-trigger`}
      >
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 w-[22rem] rounded-xl border border-gray-200 bg-white shadow-xl py-2 z-50"
          data-testid={`${testId}-panel`}
        >
          {items.map(({ to, Icon: ItemIcon, title, blurb, badge }) => (
            <Link
              key={to}
              to={to}
              onMouseEnter={() => prefetchRoute(to)}
              onClick={() => setOpen(false)}
              className={`flex items-start gap-3 px-4 py-3 transition-colors group ${colors.rowHover}`}
              data-testid={`${testId}-item-${title.toLowerCase().replace(/ /g, '-')}`}
              role="menuitem"
            >
              <div className={`mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-lg ${colors.bubbleBg} ${colors.bubbleHover} transition-colors`}>
                {ItemIcon ? <ItemIcon className="h-4 w-4" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{title}</span>
                  {badge && (
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-[#7042cc] text-white px-1.5 py-0.5 rounded-sm">
                      {badge}
                    </span>
                  )}
                </div>
                {blurb && <p className="text-xs text-gray-600 mt-0.5 leading-snug">{blurb}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default NavDropdown;
